import { PortalBillStatusType } from "../components/UpfrontPayment/types";

export interface CodeResultSerializer {
  value: string;
  abnormal_flag: "Abnormal" | "Normal";
  status?: "Final" | "Corrected" | "Processing" | "Not Ordered" | "Ordered";
}

export const SupportPhoneNumber = "(833) 408-1641";

export const NoValueProvided = "No value provided";

export const FETAL_SEX_CODE_RESULT = {
  MALE: "MALE",
  FEMALE: "FEMALE",
  MALE_MALE: "MALE & MALE",
  FEMALE_FEMALE: "FEMALE & FEMALE",
  MALE_FEMALE: "MALE & FEMALE",
  ONE_OR_BOTH_MALES: "ONE OR BOTH MALES",
  NO_RESULT: "NO RESULT",
  NO_CALL: "NO CALL",
  NOT_REPORTED: "NOT REPORTED",
  NOT_ORDERED: "NOT ORDERED",
  ORDERED: "Ordered",
  PROCESSING: "Processing",
  NA: "N/A",
  EMPTY: "",
  NO_VALUE_PROVIDED: NoValueProvided,
} as const;

export type FetalSexCodeResult =
  (typeof FETAL_SEX_CODE_RESULT)[keyof typeof FETAL_SEX_CODE_RESULT];

export interface FetalSexCodeResultSerializer {
  value: FetalSexCodeResult;
  abnormal_flag: "Abnormal" | "Normal";
  status?: "Final" | "Corrected" | "Processing" | "Not Ordered" | "Ordered";
}

export const PatientReview1ModalSeen = "PatientReview1_ModalSeen";
export const PatientReview2StarsSelected = "PatientReview2_StarsSelected";
export const PatientReview3Submitted = "PatientReview3_Submitted";
export const PatientReview4RedirectedToGoogleReview =
  "PatientReview4_RedirectedToGoogleReview";

export type UiActionType =
  | "ViewCarrierPage"
  | "ViewedDashboardVideo"
  | "ViewAneuploidyPage"
  | "ViewCfcPage"
  | "ShowFetalSex"
  | "ViewDashboard"
  | "BookConsultation"
  | "PreviewPaymentPlan"
  | "BillingVideoClicked"
  | "BillingVideoFeedback"
  | "AneuploidyVideoClicked"
  | "AneuploidyVideoFeedback"
  | "CarrierVideoClicked"
  | "CarrierVideoFeedback"
  | "RhDVideoClicked"
  | "RhDVideoFeedback"
  | "LowRiskAneuploidyVideoClicked"
  | "Del22qVideoClicked"
  | "LowRiskAneuploidyVideoFeedback"
  | "Del22qVideoFeedback"
  | "NegativeCarrierVideoClicked"
  | "RhDDetectedVideoClicked"
  | "RhDNotDetectedVideoClicked"
  | "NegativeCarrierVideoFeedback"
  | "RhDDetectedFeedback"
  | "RhDNotDetectedFeedback"
  | typeof PatientReview1ModalSeen
  | typeof PatientReview2StarsSelected
  | typeof PatientReview3Submitted
  | typeof PatientReview4RedirectedToGoogleReview;

export interface ActionSerializer {
  action_type: UiActionType;
  requisition: string;
  meta_data?: { [field: string]: string };
}

interface GestationCodeResultSerializer {
  value:
    | "SINGLETON"
    | "TWIN"
    | "Processing"
    | "UNKNOWN"
    | typeof NoValueProvided;
  abnormal_flag: "Abnormal" | "Normal";
  status?: "Final" | "Corrected" | "Processing" | "Not Ordered" | "Ordered";
}

export interface UnityReportDataJsonSerializer {
  sma_carrier?: CodeResultSerializer;
  cftr_carrier?: CodeResultSerializer;
  bthal_carrier?: CodeResultSerializer;
  athal_carrier?: CodeResultSerializer;
  fmr1_carrier?: CodeResultSerializer;
  aspa_carrier?: CodeResultSerializer;
  dmd_carrier?: CodeResultSerializer;
  elp1_carrier?: CodeResultSerializer;
  acadm_carrier?: CodeResultSerializer;
  pah_carrier?: CodeResultSerializer;
  pmm2_carrier?: CodeResultSerializer;
  dhcr7_carrier?: CodeResultSerializer;
  hexa_carrier?: CodeResultSerializer;
  fetal_sex?: CodeResultSerializer; // some fragilex reports reveal fetal sex
  carrier_report_videos?: ReportVideo[];
}

export type ReportStatus =
  | "Final"
  | "Corrected"
  | "Processing"
  | "Not Ordered"
  | "Ordered";

export type ReportSummary =
  | "Low Risk"
  | "Negative"
  | "See Report"
  | "Test Not Performed";

export interface UnityReportDataSerializer {
  report_data: UnityReportDataJsonSerializer;
  version: "v1";
  id: string;
  status: ReportStatus;

  is_amended: boolean;
  is_ordered: boolean;
  summary: ReportSummary;
  pdf_urls_by_language?: { [key: string]: string };
  // Timestamp the report was released to the ordering provider (drives the
  // "Provider reviewing" step of the results tracker). Null until released.
  provider_released_time?: string | null;
}

export interface ReportVideo {
  video_url: string;
  display_title: string;
  display_description: string;
  analytics_prefix: string;
}

export interface AneuploidyReportDataJsonSerializer {
  gestation?: GestationCodeResultSerializer;
  trisomy_21?: CodeResultSerializer;
  trisomy_18?: CodeResultSerializer;
  trisomy_13?: CodeResultSerializer;
  monosomy_x?: CodeResultSerializer;
  sex_aneuploidy?: CodeResultSerializer;
  fetal_fraction?: CodeResultSerializer;
  fetal_rhd: CodeResultSerializer;
  fetal_rhd_fa:
    | "Not Ordered"
    | "Opted out"
    | "Processing"
    | "Detected"
    | "Not Detected"
    | "See Report"
    | "Test Not Performed";
  fetal_sex: FetalSexCodeResultSerializer;
  del22q: "Opted out" | "Processing" | "See Report" | "Low Risk";
  // Backend-driven videos based on patient's test results (e.g., low risk, RhD detected).
  // Empty array [] when no videos match patient's results.
  aneuploidy_report_videos: ReportVideo[];
}

export interface AneuploidyReportDataSerializer {
  report_data: AneuploidyReportDataJsonSerializer;
  version: "v1";
  id: string;
  status: ReportStatus;

  is_amended: boolean;
  is_ordered: boolean;
  summary: ReportSummary;
  pdf_urls_by_language?: { [key: string]: string };
  // Timestamp the report was released to the ordering provider (drives the
  // "Provider reviewing" step of the results tracker). Null until released.
  provider_released_time?: string | null;
}

export interface CfcReportDataSerializer {
  id: string;
  status: ReportStatus;
  is_ordered: boolean;
  summary: ReportSummary;
  pdf_url: string | null;
}

export type BabyPeekReveal =
  | "empty_traits"
  | "hidden"
  | "pretest"
  | "pending"
  | "dismissed"
  | "available"
  | "prepaid"
  | "never"
  | "removed_prepaid"
  | "removed";

export const STANDALONE_FETAL_SEX = {
  MALE: "MALE",
  FEMALE: "FEMALE",
  MALE_MALE: "MALE & MALE",
  FEMALE_FEMALE: "FEMALE & FEMALE",
  MALE_FEMALE: "MALE & FEMALE",
  ONE_OR_BOTH_MALES: "ONE or BOTH MALES",
  NO_CALL: "NO CALL",
  NOT_REPORTED: "NOT REPORTED",
  TNP: "TNP",
  HIDDEN: "HIDDEN",
} as const;

export type StandaloneFetalSex =
  (typeof STANDALONE_FETAL_SEX)[keyof typeof STANDALONE_FETAL_SEX];

export type StandaloneFetalSexClarificationProfile =
  | "NO_CLARIFICATION"
  | "SEE_REPORT";

export const STANDALONE_FETAL_SEX_CARD_STATE = {
  HIDDEN: "HIDDEN",
  PROCESSING: "PROCESSING",
  SEX_REVEAL: "SEX_REVEAL",
  NON_SEX_RESULT: "NON_SEX_RESULT",
} as const;

// Backend-computed display state for the standalone fetal sex card. SEX_REVEAL
// is the only state allowed to enter the patient-controlled reveal flow.
export type StandaloneFetalSexCardState =
  (typeof STANDALONE_FETAL_SEX_CARD_STATE)[keyof typeof STANDALONE_FETAL_SEX_CARD_STATE];

export interface StandaloneFetalSexResultSerializer {
  standalone_fetal_sex: StandaloneFetalSex;
  standalone_fetal_sex_clarification_profile: StandaloneFetalSexClarificationProfile;
}

export interface RequisitionModelSerializer {
  req_identifier: string;
  date_received: string;
  collection_date: string;
  show_abn_form: boolean;
  physician_name: string;
  redraw_parent: string; // req_identifier of the parent
  is_unity_included?: boolean;
  is_aneuploidy_included?: boolean;
  is_rhd_included?: boolean;
  is_fetal_sex_included?: boolean;
  is_fragile_x_included?: boolean;
  is_cfc_included?: boolean;
  unity_reports?: UnityReportDataSerializer[];
  aneuploidy_reports?: AneuploidyReportDataSerializer[];
  cfc_reports?: CfcReportDataSerializer[];
  portal_bill_status: PortalBillStatusType;
  bill_pk: string;
  baby_peek_pk: string;
  reveal_free_trait: boolean;
  reveal_status: BabyPeekReveal;
  fetal_antigen_panel?: string;
  gestational_age_from_sfdc?: number;
  estimated_unity_report_date: string | null;
  estimated_aneuploidy_report_date: string | null;
  standalone_fetal_sex_result?: StandaloneFetalSexResultSerializer | null;
  standalone_fetal_sex_card_state: StandaloneFetalSexCardState;
}

export interface EmailRecipient {
  to_email: string;
  to_name: string;
}

export interface RevealGenderEmailSerializer {
  req_identifier: string;
  recipients: EmailRecipient[];
}

export type ShareFetalSexEmailSerializer = RevealGenderEmailSerializer;

export interface ContactUsEmailSerializer {
  customer_type: "patient" | "provider";
  email: string;
  first_name: string;
  inquiry_type?: "billing" | "genetic_counseling" | "test_ordering" | "other";
  last_name: string;
  message: string;
  phone: string;
}

export interface UpdateUserEmailSerializer {
  new_email: string;
}

export interface UpdateUserPasswordSerializer {
  password: string;
  email?: string;
  new_password: string;
}

export interface Auth0UserSerializer {
  email: string;
  email_verified: boolean;
  user_id: string;
  last_login: string;
  last_password_reset: string;
  access_token: string;
}

export enum PhiInsuranceStatus {
  Pending = "Pending",
  Complete = "Complete",
  Required = "Required",
  None = "None",
}

export enum PhiAddressStatus {
  Required = "Required",
  Complete = "Complete",
}

interface ClientPhiSerializer {
  first_name: string;
  last_name: string;
  dob: string;
  estimated_due_date: string;
  phone_number: string;
  ins_provider: string;
  ins_group: string;
  ins_number: string;
  subscriber_name: string;
  subscriber_date_of_birth: string;
  relationship_to_insured: string;
  ins_status: PhiInsuranceStatus;
  address_status: PhiAddressStatus;
  is_marketing_subscribed: boolean;
  is_portal_marketing_sms_subscribed: boolean;
  address: string;
  street_address_1: string;
  street_address_2: string;
  state: string;
  city: string;
  zip: string;
}

export enum ConsentStatus {
  NOT_APPLICABLE = "Not Applicable",
  COMPLETE = "Complete",
  INCOMPLETE = "Incomplete",
}

export interface ClientUserSerializer {
  id: string;
  consent_status: ConsentStatus;
  survey_status: ConsentStatus;
  dob: string;
  first_name: string;
  last_name: string;
  gender: string;
  date_joined: string;
  has_aneuploidy_consent: boolean;
  has_carrier_consent: boolean;
  phone_number: string;
  referral_code: string;
  zip: string;
  is_verified_email: boolean;
  language?: string;
}

export interface UserAuthClientResponse {
  requisitions: RequisitionModelSerializer[];
  auth0: Auth0UserSerializer;
  user: ClientUserSerializer;
  lims_id?: string;
}

interface InsuranceFileData {
  s3Url: string;
  id: string;
  valid: boolean;
  file: string;
}

export interface UserProfileAuthClientResponse {
  phi: ClientPhiSerializer;
  user?: ClientUserSerializer;
  insuranceFileUrls?: {
    front?: InsuranceFileData;
    back?: InsuranceFileData;
  };
}
